import React, { useState, useRef, useEffect } from 'react';
import { ShieldCheck, X, Loader2 } from 'lucide-react';

const OTP_LENGTH = 6;

export default function OtpPopup({ email, userId, devOtp, onVerified, onCancel }) {
  const [codes, setCodes] = useState(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...codes];
    next[index] = value.slice(-1);
    setCodes(next);
    setError('');

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (value && index === OTP_LENGTH - 1) {
      const full = next.join('');
      if (full.length === OTP_LENGTH) {
        submitOtp(full);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setCodes(next);
    setError('');

    if (pasted.length === OTP_LENGTH) {
      submitOtp(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  const submitOtp = async (code) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Doğrulama başarısız.');
        setCodes(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        return;
      }

      onVerified(data.token, data.user);
    } catch (err) {
      setError('Bağlantı hatası, tekrar deneyin.');
      console.error('OTP verify error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const full = codes.join('');
    if (full.length !== OTP_LENGTH) {
      setError('Lütfen 6 haneli kodu girin.');
      return;
    }
    submitOtp(full);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out',
    }}>
      <div style={{
        background: 'var(--bg-elevated)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border-color)',
        padding: '2rem',
        width: '100%',
        maxWidth: '420px',
        margin: '1rem',
        position: 'relative',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Close */}
        <button
          onClick={onCancel}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            padding: '4px',
          }}
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '52px',
            height: '52px',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--primary-100)',
            marginBottom: '0.75rem',
          }}>
            <ShieldCheck size={26} style={{ color: 'var(--primary-500)' }} />
          </div>
          <h3 style={{
            color: 'var(--text-primary)',
            fontSize: '1.125rem',
            fontWeight: '600',
            margin: 0,
          }}>
            E-posta Doğrulama
          </h3>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '0.875rem',
            marginTop: '0.5rem',
          }}>
            <strong>{email}</strong> adresine gönderilen 6 haneli kodu girin
          </p>
          {devOtp && (
            <p style={{
              color: 'var(--warning-500, #f59e0b)',
              fontSize: '0.875rem',
              marginTop: '0.25rem',
              fontStyle: 'italic',
            }}>
              Dev modu — Kod: {devOtp}
            </p>
          )}
        </div>

        {/* OTP inputs */}
        <form onSubmit={handleSubmit}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '1.25rem',
          }}>
            {codes.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                disabled={loading}
                style={{
                  width: '48px',
                  height: '56px',
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  borderRadius: 'var(--radius-md)',
                  border: `2px solid ${error ? 'var(--danger-400, #f87171)' : digit ? 'var(--primary-400)' : 'var(--border-color)'}`,
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  caretColor: 'var(--primary-500)',
                }}
                onFocus={(e) => {
                  if (!error) {
                    e.target.style.borderColor = 'var(--primary-400)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                  }
                }}
                onBlur={(e) => {
                  if (!error) {
                    e.target.style.borderColor = digit ? 'var(--primary-400)' : 'var(--border-color)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
            ))}
          </div>

          {error && (
            <p style={{
              color: 'var(--danger-500, #ef4444)',
              fontSize: '0.875rem',
              textAlign: 'center',
              marginBottom: '1rem',
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || codes.join('').length !== OTP_LENGTH}
            style={{
              width: '100%',
              padding: '0.8rem',
              background: 'var(--primary-600)',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: loading || codes.join('').length !== OTP_LENGTH ? 0.6 : 1,
              transition: 'background 0.15s',
            }}
          >
            {loading ? (
              <><Loader2 size={18} className="spin" /> Doğrulanıyor...</>
            ) : (
              <><ShieldCheck size={18} /> Doğrula</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
